import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import SEOHead from '../components/SEOHead';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQCategory {
  title: string;
  items: FAQItem[];
}

const faqCategories: FAQCategory[] = [
  {
    title: 'Getting Started',
    items: [
      {
        question: 'How do I create a worker account?',
        answer: 'Click "Start Getting Gigs" on the worker page and select "Worker" when registering. Your primary role, city, and minimum rate are enough for a basic profile; certifications, schedule, and media can be added later.',
      },
      {
        question: 'Is StageCrew free to use?',
        answer: 'Yes — the Free plan lets you browse gigs, create a profile, save work preferences, view relevant matches, and apply manually. The optional $9 monthly Automation Early Access plan adds supported application automation.',
      },
      {
        question: 'What information do I need for my profile?',
        answer: 'To start, add your primary role, city, and minimum hourly or daily rate. Secondary roles, union status, certifications, availability, travel preferences, and work links are optional details you can add later.',
      },
      {
        question: 'Which cities does StageCrew cover?',
        answer: 'StageCrew publishes supported market pages and sourced listings where coverage is available. Active markets and listing volume change as sources update, so check the current market list and original listing details.',
      },
    ],
  },
  {
    title: 'Job Matching & Automation',
    items: [
      {
        question: 'How does matching work?',
        answer: 'StageCrew compares your saved role, certifications, location, rate, and availability with open gigs. Results depend on the details included in each posting, and you can browse the full job board at any time.',
      },
      {
        question: 'What is auto-apply?',
        answer: 'Auto-apply is an optional early-access setting that can submit your profile to relevant postings when the destination supports it. You can pause it or choose review-first mode to stay in control. The $9 monthly plan includes up to 20 supported attempts per week.',
      },
      {
        question: 'How quickly will I get matched?',
        answer: 'Timing depends on the active postings in your market and how closely their details fit your profile. You do not need to wait for a match—you can browse and apply to active gigs from the job board.',
      },
      {
        question: 'Can I choose which gigs to apply for?',
        answer: 'Yes. Automation is optional—you can browse the gig board and apply manually, keep automation paused, or use review-first mode. Automated submission is attempted only where the posting destination supports it.',
      },
    ],
  },
  {
    title: 'Pricing & Subscriptions',
    items: [
      {
        question: 'What are the subscription plans?',
        answer: 'Free ($0/forever): browse gigs, create a profile, save preferences, view in-app matches, and apply manually. Automation Early Access ($9/month): up to 20 supported automation attempts per week with pause and review-first controls.',
      },
      {
        question: 'Can I cancel anytime?',
        answer: 'Yes — Automation Early Access is month-to-month with no contract. Request cancellation from your subscription page; access remains active until Stripe confirms the current billing period ends.',
      },
      {
        question: 'Does StageCrew take a cut of my pay?',
        answer: 'No. You keep 100% of your earnings. Employers pay you directly at the rate you set. StageCrew\'s revenue comes from worker subscriptions and employer fees — never from your paycheck.',
      },
      {
        question: 'Should I join Automation Early Access?',
        answer: 'Stay on Free if you want to browse and apply yourself. The $9 monthly early-access plan is for workers who want to try supported application automation with a 20-attempt weekly limit.',
      },
    ],
  },
  {
    title: 'Working & Payments',
    items: [
      {
        question: 'How do payments work?',
        answer: 'Employers pay you directly. Payment terms and timing are agreed between you and the employer; StageCrew does not handle, hold, or take a cut of your pay.',
      },
      {
        question: 'What is Instant Book?',
        answer: 'Instant Book is an SMS confirmation feature that appears only when StageCrew has verified delivery available. It is temporarily unavailable, so applications currently use the normal employer follow-up process.',
      },
      {
        question: 'How do ratings and reviews work?',
        answer: 'After a gig, an employer can rate your work. Available ratings are visible to employers as one piece of profile context, but StageCrew does not promise that a rating will change your match count or lead to a booking.',
      },
      {
        question: 'Do you support union workers?',
        answer: 'Yes. StageCrew supports IATSE, IBEW, and non-union workers. You can display your union status and certifications on your profile. Employers can filter by union status when searching for crew.',
      },
    ],
  },
  {
    title: 'Using the Platform',
    items: [
      {
        question: 'How can I contact an employer about a gig?',
        answer: 'Use the application destination or contact details provided with the listing. StageCrew does not currently provide a button to start a new conversation; existing conversations remain available on the Messages page.',
      },
      {
        question: 'What roles does StageCrew support?',
        answer: 'All AV, stagehand, and live event roles: stagehand, audio technician, lighting tech, gaffer, key grip, grip, rigger, camera operator, LED tech, AV technician, stage manager, production assistant, sound engineer, projectionist, and more.',
      },
      {
        question: 'Can I follow company listings?',
        answer: 'Yes. Following saves a company listing for quick reference. It does not create match updates or change your position in matching.',
      },
      {
        question: 'How do I get help or report an issue?',
        answer: 'Email support@stagecrew.app. Response times vary. For urgent gig issues, contact the employer directly using the contact information on the gig listing.',
      },
    ],
  },
];

// Build JSON-LD FAQPage structured data for SEO
const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqCategories.flatMap((cat) =>
    cat.items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    }))
  ),
};

const FAQAccordion: React.FC<{ category: FAQCategory }> = ({ category }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="mb-10">
      <h2 className="text-xl font-bold text-gray-900 mb-4">{category.title}</h2>
      <div className="space-y-2">
        {category.items.map((item, index) => (
          <div key={index} className="border border-gray-200 rounded-lg bg-white">
            <button
              onClick={() => toggle(index)}
              className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-inset rounded-lg"
              aria-expanded={openIndex === index}
            >
              <span className="font-medium text-gray-900 pr-4">{item.question}</span>
              {openIndex === index ? (
                <FaChevronUp className="text-gray-400 flex-shrink-0" aria-hidden="true" />
              ) : (
                <FaChevronDown className="text-gray-400 flex-shrink-0" aria-hidden="true" />
              )}
            </button>
            {openIndex === index && (
              <div className="px-5 pb-4 text-gray-600 leading-relaxed">
                {item.answer}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const WorkerFAQPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Worker FAQ — StageCrew | Find AV & Stagehand Gigs"
        description="Common questions about finding AV technician and stagehand gigs on StageCrew, including profiles, pricing, applications, payments, and union support."
        canonical="https://stagecrew.app/for-workers/faq"
        keywords="stagehand jobs FAQ, AV technician gigs questions, auto-apply gigs, freelance stagehand help, StageCrew worker FAQ"
        jsonLd={faqJsonLd}
      />

      {/* Header */}
      <section className="bg-gradient-to-br from-green-700 to-emerald-600 text-white py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold mb-4">Worker FAQ</h1>
          <p className="text-xl text-green-100">
            Everything you need to know about finding gigs on StageCrew.
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {faqCategories.map((category) => (
            <FAQAccordion key={category.title} category={category} />
          ))}

          {/* Still have questions */}
          <div className="bg-white rounded-lg shadow-sm p-8 text-center mt-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Still have questions?</h2>
            <p className="text-gray-600 mb-6">
              Email Edwin directly — he's happy to help you get set up and finding gigs.
            </p>
            <a
              href="mailto:edwin@stagecrew.app?subject=StageCrew%20Worker%20Question"
              className="inline-block bg-green-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-green-700 transition"
            >
              Email: edwin@stagecrew.app
            </a>
            <p className="mt-3 text-gray-500 text-sm">Response times vary.</p>
          </div>

          {/* CTA */}
          <div className="mt-10 bg-gradient-to-r from-green-700 to-emerald-700 rounded-xl p-10 text-center text-white">
            <h2 className="text-2xl font-bold mb-3">Ready to start getting gigs?</h2>
            <p className="text-green-100 mb-6">
              Create your free profile — start getting matched today.
            </p>
            <Link
              to="/register?role=worker"
              className="inline-block bg-white text-green-700 px-10 py-3 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-lg"
            >
              Start Getting Gigs — Free
            </Link>
          </div>

          {/* Quick links */}
          <div className="mt-8 text-center text-sm text-gray-500 space-x-4">
            <Link to="/for-workers" className="text-green-600 hover:underline">Worker Home</Link>
            <Link to="/help" className="text-green-600 hover:underline">General Help</Link>
            <Link to="/register?role=worker" className="text-green-600 hover:underline">Sign Up Free</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WorkerFAQPage;
